import ScheduledChangeTable from "../../../../Components/Customer/VOSS/Subscriptions/ScheduledChangeTable";
import Card from "../../../../Components/Card/Card";
import subscriptionActions from "../../../../Actions/Voss/subscriptionActions";
import customerActions from "../../../../Actions/customerActions";
import {VossScheduledChangeStatus} from "../../../../Constants/VOSS/vossScheduledChangeStatus";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import helpers from "../../../../Utils/helpers";
import React from "react";
import {InfoLabel} from "../../../../Components/Badges/InfoLabel";
import {VossSubscriptionItemTerminationSource, VossSubscriptionItemTerminationSourceText} from "../../../../Constants/VOSS/vossSubscriptionItemTerminationSource";
import {VossSubscriptionItemStatus} from "../../../../Constants/VOSS/vossSubscriptionItemStatus";
import { RankDirection} from "./SubscriptionMainInfo";
import Modals from "../../../../Constants/modals";
import useModal from "../../../../Hooks/useModal";
import Tooltip, {TooltipContent, TooltipTrigger} from "../../../../Components/Overlays/Tooltip";
import {SkynetAnchor} from "../../../../Components/Fundamentals/Anchor/SkynetAnchor";
import { VossCreditKindText} from "../../../../Constants/VOSS/vossCreditKind";
import subscriptionUtils from "../../../../Utils/subscriptionUtils";


export const ScheduledChanges = ({ blockEdit, reloadData }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { openModal } = useModal();
    const customer = useSelector((state) => state.CustomerReducer.customer);
    const subscription = useSelector((state) => state.SubscriptionReducer.subscription);
    const subscriptions = useSelector((state) => state.CustomerReducer.subscriptions);
    const subscriptionProductGroups = useSelector((state) => state.SubscriptionReducer.subscriptionProductGroups);
    const licenses = useSelector((state) => state.SubscriptionReducer.subscriptionLicenses);
    
    const isLoading = useSelector((state) => state.CommonReducer.dataLoading.subscription);
    const higherRankDependentSubscription = subscriptionUtils.findDependentByRank(subscriptions, subscription, RankDirection.HIGHER);

    const higherRankDependentHasScheduledTermination = higherRankDependentSubscription && Boolean(higherRankDependentSubscription.termination);
    const terminationSource = subscription?.termination?.source;
    const blockTerminationCancellation = Boolean(
        higherRankDependentHasScheduledTermination ||
        terminationSource === VossSubscriptionItemTerminationSource.CustomerExpiration ||
        terminationSource === VossSubscriptionItemTerminationSource.Plan
    );
    
    const onDeleteScheduledChange = (change) => {
        dispatch(subscriptionActions.deleteScheduledChange(subscription.id, change.id, () => reloadData()));
    };

    const latestScheduledChanges = subscription?.scheduledChanges?.filter(change =>
        change.status === VossScheduledChangeStatus.Pending ||
        change.status === VossScheduledChangeStatus.CompletedForUpcomingPeriod
    );

    const onDeleteProductGroupTermination = (productGroupId) => {
        dispatch(subscriptionActions.deleteProductGroupTermination(subscription.id, productGroupId, () => {
            dispatch(subscriptionActions.getSubscriptionProductGroups(subscription.id, VossSubscriptionItemStatus.Active));
        }));
    }

    const onDeleteLicenseTermination = (licenseId) => {
        dispatch(subscriptionActions.deleteLicenseTermination(subscription.id, licenseId, () => {
            dispatch(subscriptionActions.getSubscriptionLicenses(customer.vossCustomer.id, subscription.id, VossSubscriptionItemStatus.Active));
        }));
    }
    
    // Normalize all terminations into a single list with unified structure
    const terminations = React.useMemo(() => {
        const terminations = [];
        
        // Add subscription termination
        if (subscription?.termination) {
            terminations.push({
                id: subscription.id,
                type: 'subscription',
                name: subscription.package.name,
                termination: subscription.termination,
                date: helpers.formatDate(subscription.termination.terminationDate),
                onDelete: () => openModal(Modals.ConfirmDeleteModal, {
                    onConfirm: () => dispatch(subscriptionActions.deleteSubscriptionTermination(subscription.id, reloadData))
                }),
                canCancel: !blockTerminationCancellation,
                cancelDisabledReason: subscription.termination ?
                    t('editsubscriptioncontainer.terminationCancellationNotAllowed', {
                        value1: t(VossSubscriptionItemTerminationSourceText[subscription.termination.source])
                    })
                    :
                    t('editsubscriptioncontainer.higherTerminationDependencyRestriction', {
                        packageName: higherRankDependentSubscription?.package.name
                    })
            });
        }
        
        // Add product group terminations
        if (subscriptionProductGroups) {
            subscriptionProductGroups
                .filter(pg => pg.termination)
                .forEach(pg => {
                    terminations.push({
                        id: pg.id,
                        type: 'productGroup',
                        name: pg.name,
                        termination: pg.termination,
                        date: helpers.formatDate(pg.termination.terminationDate),
                        onDelete: () => onDeleteProductGroupTermination(pg.id),
                        canCancel: !blockEdit
                    });
                });
        }
        
        // Add license terminations
        if (licenses) {
            licenses.forEach(productGroup => {
                productGroup.licenses
                    .filter(lic => lic.termination)
                    .forEach(lic => {
                        terminations.push({
                            id: lic.id,
                            type: 'license',
                            name: productGroup.product.name,
                            termination: lic.termination,
                            date: helpers.formatDate(lic.termination.scheduledDate),
                            onDelete: () => onDeleteLicenseTermination(lic.id),
                            canCancel: !blockEdit
                        });
                    });
            });
        }
        
        return terminations;
    }, [subscription, subscriptionProductGroups, licenses, blockEdit, blockTerminationCancellation, higherRankDependentSubscription, t]);

    const getTerminationTypeLabel = (type) => {
        switch (type) {
            case 'subscription':
                return t("subscriptions.subscription");
            case 'productGroup':
                return t("scheduledChangeTable.productGroup");
            case 'license':
                return t("scheduledChangeTable.license");
            default:
                return '';
        }
    };

    const hasAnyChanges = terminations.length > 0 || latestScheduledChanges?.length > 0;
    if (!subscription) return;
    
    return (
        <Card>
            <Card.CHeader openDefault={hasAnyChanges}>
                <Card.Header.Title>
                    {t("scheduledChangeTable.upcomingChanges")}
                    {subscription?.termination &&
                        <div className="d-inline-block position-relative infolabel ml-8">
                            <InfoLabel type={"warning"} title={t("editsubscriptioncontainer.subscriptionIsTerminated")}/>
                        </div>
                    }
                </Card.Header.Title>
            </Card.CHeader>
            <Card.Content isLoading={isLoading}>
                <h3>{t("scheduledChangeTable.terminations")}</h3>
                <table className={`table table-stripe skynet-table-no-divider mb-24 ${blockEdit ? 'disabled' : ''}`}>
                    <thead>
                        <tr>
                            <th>{t("scheduledChangeTable.regarding")}</th>
                            <th>{t("scheduledChangeTable.reason")}</th>
                            <th>{t("scheduledChangeTable.subReason")}</th>
                            <th>{t("scheduledChangeTable.comment")}</th>
                            <th>{t("scheduledChangeTable.credit")}</th>
                            <th>{t("scheduledChangeTable.terminationType")}</th>
                            <th>{t("scheduledChangeTable.date")}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                    {terminations.length > 0 ? (
                        terminations.map((item) => (
                            <tr key={`${item.type}-${item.id}`}>
                                <td>{item.name} ({getTerminationTypeLabel(item.type)})</td>
                                <td>{item.termination.reason?.reasonName}</td>
                                <td>{item.termination.reason?.subReasonName}</td>
                                <td>{item.termination.reason?.comment}</td>
                                <td>{t(VossCreditKindText[item.termination.creditOptions.creditKind])}</td>
                                <td>{t(VossSubscriptionItemTerminationSourceText[item.termination.source])}</td>
                                <td className={"text-nowrap"}>{item.date}</td>
                                <td width={200} className="text-center">
                                    {item.canCancel ? (
                                        <SkynetAnchor onClick={item.onDelete}>
                                            {t('common.cancel')}
                                        </SkynetAnchor>
                                    ) : (
                                        <Tooltip>
                                            <TooltipTrigger>
                                                <SkynetAnchor disabled={true}>
                                                    {t('common.cancel')}
                                                </SkynetAnchor>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                {item.cancelDisabledReason}
                                            </TooltipContent>
                                        </Tooltip>
                                    )}
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={10}>
                                <em>{t("scheduledChangeTable.noScheduledChanges")}</em>
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
                <h3>{t("scheduledChangeTable.scheduledChanges")}</h3>
                <ScheduledChangeTable scheduledChanges={latestScheduledChanges} onDelete={(change) => onDeleteScheduledChange(change)} disabled={blockEdit} isLoading={isLoading} />
            </Card.Content>
        </Card>
    )
    
}